/** Quote Matching & Distribution rule workflow — validation, save, coverage, change history */

import {
  DEVELOPER_NAME,
  MESSAGES,
  createDefaultQuoteMatchingForm,
  deriveQuoteMatchingDisplayStatus,
  evaluateConditionCoverage,
  findDuplicateRuleName,
  findOverlappingActiveRule,
  formatConditionsDisplay,
  getReferencedCategoryIds,
  getTradeCategoryById,
  nextQuoteMatchingRuleId,
  normalizeCondition,
  normalizeQuoteMatchingForm,
} from './quoteMatchingDistributionConfig';
import { emitFieldChangesHistoryEntry } from './changeHistoryEmitters';

const nowIso = () => new Date().toISOString();

const TRACKED_KEYS = ['ruleName', 'ruleStatus', 'conditions', 'quoteHistoryDays'];

export const getChangedFields = (previous, proposed) => {
  if (!previous) return TRACKED_KEYS.slice();
  return TRACKED_KEYS.filter((key) => {
    const a = previous[key];
    const b = proposed[key];
    if (key === 'conditions') {
      const norm = (list) =>
        (list || []).map((c) => ({
          operator: c.operator,
          tradeCategoryId: c.tradeCategoryId,
        }));
      return JSON.stringify(norm(a)) !== JSON.stringify(norm(b));
    }
    if (key === 'quoteHistoryDays') {
      return String(a ?? '') !== String(b ?? '');
    }
    if (key === 'ruleName') {
      return String(a || '').trim() !== String(b || '').trim();
    }
    return a !== b;
  });
};

export const validateQuoteMatchingRule = (
  form,
  existingRules = [],
  excludeId,
  { isEdit } = {},
) => {
  const errors = {};
  const name = String(form.ruleName || '').trim();
  const id = excludeId || form.id;

  if (!name) errors.ruleName = 'Enter a Rule Name.';
  else if (name.length > 100) errors.ruleName = 'Rule Name must be 100 characters or fewer.';
  else if (findDuplicateRuleName(existingRules, name, id)) {
    errors.ruleName = MESSAGES.duplicateName;
  }

  if (form.ruleStatus !== 'active' && form.ruleStatus !== 'inactive') {
    errors.ruleStatus = 'Select Active or Inactive.';
  }

  const conditions = (form.conditions || []).map(normalizeCondition);
  conditions.forEach((row, index) => {
    if (!row.operator || !row.tradeCategoryId) {
      errors[`conditions.${index}`] = 'Select an Operator and a Trade Category.';
    }
  });

  const coverage = evaluateConditionCoverage(conditions);
  if (coverage.repeated) {
    errors.conditions = MESSAGES.repeatedCondition;
  } else if (coverage.redundant) {
    errors.conditions = MESSAGES.redundantCondition;
  } else if (coverage.impossible) {
    errors.conditions = MESSAGES.impossibleConditions;
  }

  const refs = getReferencedCategoryIds(conditions);
  const unavailable = refs.filter((refId) => !getTradeCategoryById(refId));
  if (unavailable.length && form.ruleStatus === 'active') {
    errors.conditions = MESSAGES.unavailableReference;
  }

  const historyRaw = String(form.quoteHistoryDays ?? '').trim();
  const historyNum = Number(historyRaw);
  if (!historyRaw || !Number.isInteger(historyNum) || historyNum <= 0) {
    errors.quoteHistoryDays = MESSAGES.invalidHistory;
  }

  if (isEdit) {
    const reason = String(form.changeReason || '').trim();
    if (!reason) errors.changeReason = 'Enter a Change Reason.';
    else if (reason.length > 500) {
      errors.changeReason = 'Change Reason must be 500 characters or fewer.';
    }
  }

  if (form.ruleStatus === 'active' && !errors.conditions && !coverage.incompleteRows) {
    if (!coverage.coveredIds.length) {
      errors.conditions = MESSAGES.noCoveredCategory;
    } else {
      const conflict = findOverlappingActiveRule(existingRules, conditions, id);
      if (conflict) {
        const otherId = conflict.rule?.id || '—';
        errors.conditions = conflict.exactSame
          ? `${MESSAGES.duplicateCoverage}: ${otherId}.`
          : `${MESSAGES.overlap}: ${otherId}.`;
      }
    }
  }

  return errors;
};

/** @deprecated Use validateQuoteMatchingRule */
export const validateAllSteps = (form, existingRules, excludeId, options) =>
  validateQuoteMatchingRule(form, existingRules, excludeId, options);

const buildChangeHistoryEntry = ({ action, previous, next, changeReason, actor, stamp }) => {
  const changes = [];
  if (action === 'Add') {
    changes.push({
      field: 'Rule',
      previousValue: '—',
      newValue: `${next.id} — ${next.ruleName}`,
    });
  } else {
    const changed = getChangedFields(previous, next);
    changed.forEach((key) => {
      if (key === 'conditions') {
        changes.push({
          field: 'Conditions',
          previousValue: formatConditionsDisplay(previous.conditions || []),
          newValue: formatConditionsDisplay(next.conditions || []),
        });
      } else if (key === 'quoteHistoryDays') {
        changes.push({
          field: 'Quote History',
          previousValue: String(previous.quoteHistoryDays || '—'),
          newValue: String(next.quoteHistoryDays || '—'),
        });
      } else if (key === 'ruleName') {
        changes.push({
          field: 'Rule Name',
          previousValue: previous.ruleName || '—',
          newValue: next.ruleName || '—',
        });
      } else if (key === 'ruleStatus') {
        changes.push({
          field: 'Rule Status',
          previousValue: previous.ruleStatus === 'inactive' ? 'Inactive' : 'Active',
          newValue: next.ruleStatus === 'inactive' ? 'Inactive' : 'Active',
        });
      }
    });
  }

  return {
    action,
    changeReason: changeReason || '—',
    changes,
    actor,
    timestamp: stamp,
    status: next.status,
  };
};

export const mergeRulesAfterSave = (rules, form, { isEdit, existingId, openedUpdatedOn } = {}) => {
  const normalized = normalizeQuoteMatchingForm(form);
  const stamp = nowIso();
  const id = isEdit && existingId ? existingId : nextQuoteMatchingRuleId(rules);
  const previous = isEdit ? rules.find((r) => r.id === existingId) : null;

  if (isEdit && previous && openedUpdatedOn) {
    const previousStamp = previous.updatedOn || previous.lastUpdatedOn || '';
    if (previousStamp && previousStamp !== openedUpdatedOn) {
      return { ok: false, error: 'stale', message: MESSAGES.staleEdit, rules };
    }
  }

  const conditions = (normalized.conditions || [])
    .map(normalizeCondition)
    .filter((c) => c.operator && c.tradeCategoryId);

  const draft = {
    ...createDefaultQuoteMatchingForm(),
    ...normalized,
    id,
    ruleName: String(normalized.ruleName || '').trim(),
    ruleStatus: normalized.ruleStatus === 'inactive' ? 'inactive' : 'active',
    conditions,
    quoteHistoryDays: String(Number(String(normalized.quoteHistoryDays).trim())),
    changeReason: '',
  };

  const rulesForStatus = isEdit
    ? rules.map((r) => (r.id === existingId ? draft : r))
    : [...rules, draft];
  draft.status = deriveQuoteMatchingDisplayStatus(draft, rulesForStatus);

  const settingsChanged = isEdit ? getChangedFields(previous, draft).length > 0 : true;

  if (isEdit && !settingsChanged) {
    const refreshedStatus = deriveQuoteMatchingDisplayStatus(previous, rules);
    const refreshed = { ...previous, status: refreshedStatus };
    const statusChanged = refreshedStatus !== previous.status;
    return {
      ok: true,
      unchanged: true,
      revalidated: statusChanged,
      saved: refreshed,
      rules: statusChanged
        ? rules.map((r) => (r.id === existingId ? refreshed : r))
        : rules,
      message: MESSAGES.updated,
    };
  }

  const historyEntry = buildChangeHistoryEntry({
    action: isEdit ? 'Edit' : 'Add',
    previous,
    next: draft,
    changeReason: String(form.changeReason || '').trim(),
    actor: DEVELOPER_NAME,
    stamp,
  });

  const saved = {
    ...draft,
    createdBy: previous?.createdBy || DEVELOPER_NAME,
    createdOn: previous?.createdOn || stamp,
    updatedBy: DEVELOPER_NAME,
    updatedOn: stamp,
    changeHistory: [...(previous?.changeHistory || []), historyEntry],
  };

  const rulesAfterSave = isEdit
    ? rules.map((r) => (r.id === existingId ? saved : r))
    : [...rules, saved];
  saved.status = deriveQuoteMatchingDisplayStatus(saved, rulesAfterSave);

  emitFieldChangesHistoryEntry({
    localEntry: historyEntry,
    module: 'Configuration — Quote Matching & Distribution',
    sourceModuleRef: 'configuration_quote_matching',
    changedItem: saved.ruleName || saved.id,
    itemId: saved.id,
  });

  const nextRules = isEdit
    ? rules.map((r) => (r.id === existingId ? saved : r))
    : [...rules, saved];

  return {
    ok: true,
    unchanged: false,
    saved,
    rules: nextRules,
    message: isEdit ? MESSAGES.updated : MESSAGES.saved,
    historyEntry,
  };
};
